import { useEffect, useRef } from "react";

// Keeps the device screen awake the entire time the app is open.
// 1) Screen Wake Lock API where supported (Chrome, Android, newer Safari).
// 2) iOS fallback: iPhones (especially home-screen PWAs) often ignore the
//    Wake Lock API, so on iOS we also play an invisible 64x64 muted looping
//    video — iOS treats it as active media and never auto-locks the screen
//    (the NoSleep technique). Without this, long Bible read-aloud sessions
//    get cut off when the phone locks and the app is kicked to the Home
//    Screen.
const IS_IOS = typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

export default function ScreenAwake() {
  const lockRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const acquire = async () => {
      try {
        if (typeof navigator === "undefined" || !navigator.wakeLock?.request) return;
        if (lockRef.current) return;
        if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
        const wl = await navigator.wakeLock.request("screen");
        if (cancelled) { try { await wl.release(); } catch (_) {} return; }
        lockRef.current = wl;
        if (wl.addEventListener) {
          wl.addEventListener("release", () => {
            if (lockRef.current === wl) lockRef.current = null;
          });
        }
      } catch (_) {
        // request can reject if the document isn't focused/visible —
        // the visibilitychange handler will retry when it becomes so.
      }
    };

    // iOS fallback: invisible looping muted video keeps the display on.
    const ensureVideo = () => {
      if (!IS_IOS || cancelled) return;
      let v = videoRef.current;
      if (!v) {
        v = document.createElement("video");
        v.setAttribute("playsinline", "");
        v.setAttribute("muted", "");
        v.muted = true;
        v.loop = true;
        v.autoplay = true;
        v.src = "/nosleep.mp4";
        v.style.cssText = "position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;bottom:0;right:0;z-index:-1;";
        v.setAttribute("aria-hidden", "true");
        v.setAttribute("data-testid", "screen-awake-video");
        document.body.appendChild(v);
        videoRef.current = v;
      }
      if (v.paused) v.play().catch(() => {});
    };

    const onVisibility = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        acquire();
        ensureVideo();
      }
    };
    const onGesture = () => { ensureVideo(); };

    acquire();
    ensureVideo();
    // Belt & suspenders: wake locks silently drop on iOS — re-check every 30s.
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        if (!lockRef.current) acquire();
        ensureVideo();
      }
    }, 30000);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("focus", onVisibility);
      document.addEventListener("touchstart", onGesture, { passive: true });
      document.addEventListener("click", onGesture, { passive: true });
    }

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
        window.removeEventListener("focus", onVisibility);
        document.removeEventListener("touchstart", onGesture);
        document.removeEventListener("click", onGesture);
      }
      const wl = lockRef.current;
      lockRef.current = null;
      if (wl) { try { wl.release(); } catch (_) {} }
      const v = videoRef.current;
      videoRef.current = null;
      if (v) { try { v.pause(); v.remove(); } catch (_) {} }
    };
  }, []);

  return null;
}
